import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum Theme {
    Light = 'light',
    Dark = 'dark',
    System = 'system',
}

const initTheme = () => {
    if (typeof window === 'undefined') {
        return Theme.System;
    }
    try {
        return (localStorage.getItem('theme') as Theme) ?? Theme.System;
    } catch {
        // Storage blocked (privacy settings, sandboxed iframe): fall back to the OS theme.
        return Theme.System;
    }
};

// Same-tab sync: useTheme keeps local state, so multiple mounted instances (e.g. the
// desktop header control and the mobile menu control) must be told when one changes.
const THEME_CHANGE_EVENT = 'cm-theme-change';

export const useTheme = () => {
    const { t } = useTranslation();
    const [theme, setTheme] = useState(initTheme);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handleThemeChange = (event: Event) => {
            setTheme((event as CustomEvent<Theme>).detail);
        };
        window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
        return () => window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    }, []);

    const themeOptions = [
        { label: t('Light Theme'), icon: 'light', value: Theme.Light },
        { label: t('Dark Theme'), icon: 'dark', value: Theme.Dark },
        { label: t('System Theme'), icon: 'systemTheme', value: Theme.System },
    ] as const;
    const currentTheme = themeOptions.find(({ value }) => value === theme) as (typeof themeOptions)[number];

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const isDark =
            theme === Theme.Dark ||
            (theme === Theme.System && window.matchMedia('(prefers-color-scheme: dark)').matches);

        document.documentElement.classList.toggle('dark', isDark);
    }, [theme]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event: MediaQueryListEvent) => {
            if (theme === Theme.System) {
                document.documentElement.classList.toggle('dark', event.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const updateTheme = (value: Theme) => {
        setTheme(value);

        try {
            if (value === Theme.System) {
                localStorage.removeItem('theme');
            } else {
                localStorage.setItem('theme', value);
            }
        } catch {
            // Storage blocked: the choice still applies for this visit, it just isn't remembered.
        }

        // Notify other mounted useTheme instances so their displayed selection stays in sync.
        window.dispatchEvent(new CustomEvent<Theme>(THEME_CHANGE_EVENT, { detail: value }));
    };

    return { theme, themeOptions, currentTheme, setTheme: updateTheme };
};
